<template>
  <div class="mirco-app-report preview">
    <!-- 只有当 getUrl 有值时才渲染 micro-app，避免空 url -->
    <micro-app
      v-if="getUrl"
      :name="microName"
      :url="getUrl"
      iframe
      router-mode="native"
      :baseroute="`/${microName}`"
      :data="storeInfo"
      keep-alive
      @datachange="handleDataChange"
    ></micro-app>
    <!-- 可选：加载中提示 -->
    <div v-else class="loading">Loading...</div>
  </div>
</template>
<script lang="ts">
import microApp from '@micro-zoe/micro-app';
import configEnv from '@/config/user.env';
import { useStore } from 'vuex';
import { computed, defineComponent, nextTick, onMounted, reactive, ref, toRefs, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import eventBus from '@/utils/eventBus';
import { lotsUIEventInstance } from '@annto/ui';
import { logout } from '@/utils/index';
import { handleMicroBigDataTrack } from '@/utils/bigLogData/trackHelper';
import { getConfig } from '@/config/role-config';
import { getDictList } from '@/api/userApi';

export default defineComponent({
  setup() {
    const storeInfo = ref({});
    const state = reactive({
      router: {},
      user: null,
      authority: null,
      currentTenant: null,
      appId: null,
      appList: null,
      btnPermissions: null,
      path: '',
      query: {},
      applicationCode: '',
      currentRoleDic: {}, //当前角色信息下发给子系统
    });
    const store = useStore();

    const route = useRoute();
    const router = useRouter();
    const microName = 'microLsrm';
    const dynamicUrl = ref<string | null>(null); // 👈 用于存储从接口获取的 URL

    watch(route, (val) => {
      initPage();
    });

    microApp.getData(microName);

    const getUrl = computed(() => {
      // return dynamicUrl.value;
      return configEnv.microLsrmUrl;
      // return 'https://localhost.annto.com:8080/lsrm.html/';
    });
    // 获取子应用 URL 的方法
    const fetchMicroAppUrl = async () => {
      try {
        // 获取v3开关试点字典数据
        const dicRes = await getDictList({ dictCodes: 'EL_SWITCH_CODE' });
        let _VUE3_SWITCH_LSRM = false; // LSRM_VUE3试点总开关
        if (+dicRes.code === 0 && dicRes.data) {
            const EL_SWITCH_CODE = dicRes.data.EL_SWITCH_CODE || {};
            _VUE3_SWITCH_LSRM = EL_SWITCH_CODE.VUE3_SWITCH_LSRM === '1'; // LSRM_VUE3试点总开关
        }
        if (_VUE3_SWITCH_LSRM) {
          dynamicUrl.value = configEnv.microLsrmUrl;
        } else {
          dynamicUrl.value = configEnv.microElLsrmUrl;
        }
      } catch (error) {
        // console.error('Failed to fetch micro app URL:', error);
        // 回退到EL LSRM url 默认值
        dynamicUrl.value = configEnv.microElLsrmUrl;
      } finally {
      }
    };

    const initPage = () => {
      const routeInfo = microApp.router.current.get(microName);
      if (routeInfo && microApp.router.decode(route.path).includes(microName)) {
        if (`/#${route.path}` !== routeInfo.fullPath) {
          // 把nextTick改成setTimeout可解决再去登录的时候，停留在我上个账号最后访问的页面问题
          setTimeout(() => {
            microApp.router.replace({ name: microName, path: `/#${route.fullPath}`, params: route.query || {} });
          });
        }
      }

      state.router = router; // 假设 router 是从 useRouter 获取的
      state.user = store.getters.user; // 从 Vuex 获取用户信息
      state.authority = store.getters.authority;
      state.currentTenant = store.getters.currentTenant;
      state.appId = store.getters.appId;
      state.appList = store.getters.appList;
      state.btnPermissions = store.getters.btnPermissions;
      state.applicationCode = store.state.applicationCode;
      state.path = route.path;
      state.query = route.query;
      // 获取当前角色配置信息
      const roleItem = store.state.roleList.find((item: { roleCode: any }) => item.roleCode === store.state.curRole);
      // 提取当前角色信息的code和name
      const roleDataDic = { 
        code: store?.state?.curRole ?? '',
        name: roleItem?.roleName ?? ''
      };
      // 下发给子系统的当前角色配置信息的code和name
      state.currentRoleDic = roleDataDic;

      const { user, authority, currentTenant, appId, appList, btnPermissions, path, query, applicationCode, currentRoleDic } =
        toRefs(state);
      // 这里的一些参数除了重点说明的两个一般物流的项目都会有;
      storeInfo.value = {
        system: 'EL', // 下发基座系统名称
        type: 'updateData',
        router,
        user,
        authority,
        currentTenant,
        appId,
        appList,
        btnPermissions,
        path,
        query,
        applicationCode,
        lotsUIEventInstance,
        currentRoleDic,
      };
    };

    const handleDataChange = (val: any) => {
      const data = val.detail.data;
      if (handleMicroBigDataTrack(data, route.path)) return;
      // 子应用可以通过这个函数关闭当前页面
      if (data.type === 'closeActiveView') {
        const { view } = data;
        const findView = store.getters.visitedViews.find((item: any) => {
          return item.href === view.href;
          // return item.href.replace('#', '') === view.fullPath;
        });
        if (findView && findView.fullPath) {
          store.dispatch('delView', findView);
        }
      } else if (data.type === 'routes') {
        const { routes, lang } = data;
        store.commit('updateMicroRoutes', { microName, routes, lang });
        store.commit('UPDATE_VISITED_TITLE');
      } else if (data.type === 'logout') {
        logout();
      }
    };

    // 监听关闭标签事件
    const closeSelectedTag = (view: any) => {
      microApp.setData(microName, { type: 'closeSelectedTag', view });
    };
    eventBus.$on('closeSelectedTag', closeSelectedTag);

    onMounted(async () => {
      // 记得修改子应用 id
      microApp.setData(microName, { path: route.path, query: route.query || {} });
      initPage();
        // await fetchMicroAppUrl(); // 先获取 URL
        // if (dynamicUrl.value) {
        //   microApp.setData(microName, { path: route.path, query: route.query || {} });
        //   initPage();
        // }
    });

    return {
      microName,
      storeInfo,
      getUrl,
      handleDataChange,
    };
  },
});
</script>
<style lang="less">
.mirco-app-report {
  height: 100%;

  micro-app[name='microLsrm'] {
    width: 100%;
    height: 100%;

    .root-panel {
      height: 100%;
    }
    .layout-content {
      margin-top: 0;
    }
    .vxe-table--render-default .vxe-table--fixed-right-wrapper.scrolling--middle {
      box-shadow: -8px 0px 10px -5px rgba(0, 0, 0, 0.12);
    }
    .lsrm-app {
      .md-range-editor--small.md-input__inner {
        height: 24px;
      }
    }
  }
}
</style>
